import { useState } from "react";
import { Mail, Phone, MapPin, Send, Info } from "lucide-react";
import { api, formatApiError, useAuth } from "@/lib/api";
import { toast } from "sonner";
import PageIntro from "@/components/stagiaire/PageIntro";

export default function StagiaireContact() {
    const { user } = useAuth();
    const [form, setForm] = useState({
        name: user ? `${user.first_name || ""} ${user.last_name || ""}`.trim() : "",
        email: user?.email || "",
        phone: user?.phone || "",
        subject: "",
        message: "",
    });
    const [submitting, setSubmitting] = useState(false);
    const [sent, setSent] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!form.subject.trim() || !form.message.trim()) {
            toast.error("Merci de renseigner un sujet et un message.");
            return;
        }
        setSubmitting(true);
        try {
            await api.post("/contact", {
                name: form.name,
                email: form.email,
                phone: form.phone,
                subject: `[Stagiaire] ${form.subject}`,
                message: form.message,
            });
            toast.success("Message envoyé — nous revenons vers vous sous 48h.");
            setSent(true);
            setForm((f) => ({ ...f, subject: "", message: "" }));
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="mx-auto max-w-4xl px-6 py-10" data-testid="page-stagiaire-contact">
            <PageIntro
                eyebrow="Nous contacter"
                title="Une question ? Nous sommes là."
                description="Cette page vous permet de joindre directement l'équipe Cursus Physio. Un dossier administratif à régler, une session à modifier, une attestation manquante, un problème technique — écrivez-nous, nous vous répondons sous 48h."
                tips={[
                    "Précisez la formation ou session concernée dans votre message si applicable.",
                    "Pour une réclamation qualité (Qualiopi), utilisez plutôt le formulaire dédié dans « Mes documents ».",
                ]}
            />

            <div className="grid gap-6 md:grid-cols-[1.4fr_1fr]">
                {/* Formulaire */}
                <form
                    onSubmit={submit}
                    className="rounded-3xl border border-brand-line bg-white p-6 shadow-premium sm:p-8"
                    data-testid="stagiaire-contact-form"
                >
                    <div className="grid gap-4 sm:grid-cols-2">
                        <div>
                            <label className="text-[11px] font-semibold uppercase tracking-wider text-brand-smoke">
                                Nom complet
                            </label>
                            <input
                                type="text"
                                value={form.name}
                                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                                required
                                data-testid="stagiaire-contact-name"
                                className="mt-2 w-full rounded-xl border border-brand-line px-4 py-2.5 outline-none focus:border-brand-orange focus:ring-2 focus:ring-brand-orange/20"
                            />
                        </div>
                        <div>
                            <label className="text-[11px] font-semibold uppercase tracking-wider text-brand-smoke">
                                Email
                            </label>
                            <input
                                type="email"
                                value={form.email}
                                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                                required
                                data-testid="stagiaire-contact-email"
                                className="mt-2 w-full rounded-xl border border-brand-line px-4 py-2.5 outline-none focus:border-brand-orange focus:ring-2 focus:ring-brand-orange/20"
                            />
                        </div>
                    </div>

                    <div className="mt-4">
                        <label className="text-[11px] font-semibold uppercase tracking-wider text-brand-smoke">
                            Téléphone (optionnel)
                        </label>
                        <input
                            type="tel"
                            value={form.phone}
                            onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                            data-testid="stagiaire-contact-phone"
                            className="mt-2 w-full rounded-xl border border-brand-line px-4 py-2.5 outline-none focus:border-brand-orange focus:ring-2 focus:ring-brand-orange/20"
                        />
                    </div>

                    <div className="mt-4">
                        <label className="text-[11px] font-semibold uppercase tracking-wider text-brand-smoke">
                            Sujet
                        </label>
                        <input
                            type="text"
                            value={form.subject}
                            onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))}
                            required
                            placeholder="Ex : Attestation manquante — session Paris 22/08"
                            data-testid="stagiaire-contact-subject"
                            className="mt-2 w-full rounded-xl border border-brand-line px-4 py-2.5 outline-none focus:border-brand-orange focus:ring-2 focus:ring-brand-orange/20"
                        />
                    </div>

                    <div className="mt-4">
                        <label className="text-[11px] font-semibold uppercase tracking-wider text-brand-smoke">
                            Votre message
                        </label>
                        <textarea
                            rows={7}
                            value={form.message}
                            onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
                            required
                            placeholder="Décrivez votre demande — contexte, dates, pièces attendues…"
                            data-testid="stagiaire-contact-message"
                            className="mt-2 w-full resize-none rounded-xl border border-brand-line px-4 py-3 outline-none focus:border-brand-orange focus:ring-2 focus:ring-brand-orange/20"
                        />
                    </div>

                    <button
                        type="submit"
                        disabled={submitting}
                        data-testid="stagiaire-contact-submit"
                        className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-brand-orange px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-[#d9772c] disabled:opacity-60 sm:w-auto"
                    >
                        <Send className="h-4 w-4" />
                        {submitting ? "Envoi…" : "Envoyer mon message"}
                    </button>

                    {sent && (
                        <div
                            className="mt-4 flex items-start gap-2 rounded-xl border border-green-200 bg-green-50 p-3 text-sm text-green-900"
                            data-testid="stagiaire-contact-sent"
                        >
                            <Info className="mt-0.5 h-4 w-4 flex-shrink-0" />
                            Message envoyé — nous revenons vers vous sous 48h ouvrées.
                        </div>
                    )}
                </form>

                {/* Coordonnées directes */}
                <aside className="space-y-4">
                    <div className="rounded-2xl border border-brand-line bg-white p-6 shadow-premium">
                        <div className="text-[11px] font-semibold uppercase tracking-[0.22em] text-brand-orange">
                            Contact direct
                        </div>
                        <ul className="mt-4 space-y-4 text-sm">
                            <li className="flex items-start gap-3">
                                <Mail className="mt-0.5 h-4 w-4 flex-shrink-0 text-brand-orange" />
                                <div>
                                    <div className="text-[10px] uppercase tracking-wider text-brand-smoke">Email</div>
                                    <a
                                        href="mailto:contact@cursus-physio.fr"
                                        className="mt-0.5 block font-medium text-brand-ink hover:text-brand-orange"
                                    >
                                        contact@cursus-physio.fr
                                    </a>
                                </div>
                            </li>
                            <li className="flex items-start gap-3">
                                <Phone className="mt-0.5 h-4 w-4 flex-shrink-0 text-brand-orange" />
                                <div>
                                    <div className="text-[10px] uppercase tracking-wider text-brand-smoke">
                                        Téléphone
                                    </div>
                                    <a
                                        href="tel:+33184801205"
                                        className="mt-0.5 block font-medium text-brand-ink hover:text-brand-orange"
                                    >
                                        01 84 80 12 05
                                    </a>
                                    <div className="mt-0.5 text-xs text-brand-smoke">Lun-Ven · 9h-18h</div>
                                </div>
                            </li>
                            <li className="flex items-start gap-3">
                                <MapPin className="mt-0.5 h-4 w-4 flex-shrink-0 text-brand-orange" />
                                <div>
                                    <div className="text-[10px] uppercase tracking-wider text-brand-smoke">
                                        Siège
                                    </div>
                                    <div className="mt-0.5 font-medium text-brand-ink">
                                        Cursus Physio — L&apos;Institut de la Prévention Active
                                    </div>
                                </div>
                            </li>
                        </ul>
                    </div>

                    <div className="rounded-2xl border border-brand-orange/25 bg-brand-orange/[0.05] p-5 text-sm text-brand-ink/85">
                        <div className="font-semibold text-brand-ink">Une réclamation qualité ?</div>
                        <p className="mt-1 text-xs text-brand-ink/70">
                            Pour toute réclamation Qualiopi (contenu de formation, conditions d&apos;accueil,
                            formateur…), utilisez le formulaire dédié dans « Mes documents » pour un traitement
                            traçable.
                        </p>
                    </div>
                </aside>
            </div>
        </div>
    );
}
